import React, { useState } from "react";
import { Alert, Autocomplete, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { checkGoldenSetCsv, downloadSampleCsv } from "../helpers/goldenSetCsv";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export const describeGoldenSet = (g) =>
  [
    plural(g.rows, "question", "questions"),
    plural(g.scripts.length, "script", "scripts"),
    g.has_line_order ? "dialogue order" : "no dialogue order",
    `added ${new Date(g.created_at).toLocaleDateString()}`,
  ].join(" · ");

// Matches the golden set name or any of its script names.
const matches = (g, text) => {
  const q = text.trim().toLowerCase();
  return !q || g.name.toLowerCase().includes(q) || g.scripts.some((s) => s.toLowerCase().includes(q));
};

const AddGoldenSetForm = ({ onAdd, onCancel }) => {
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [check, setCheck] = useState(null);
  const [adding, setAdding] = useState(false);

  const handleFile = async (picked) => {
    setFile(picked || null);
    setCheck(picked ? checkGoldenSetCsv(await picked.text()) : null);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await onAdd({ name: name.trim(), csvContent: await file.text(), csvFilename: file.name });
    } finally {
      setAdding(false);
    }
  };

  const canAdd = name.trim() && check?.ok && !adding;

  return (
    <Box component="form" onSubmit={handleAdd} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle2">Add a golden set</Typography>
        <TextField label="Golden set name" value={name} onChange={(e) => setName(e.target.value)} size="small" />
        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
          {file ? file.name : "Choose a CSV file"}
          <input type="file" accept=".csv" hidden onChange={(e) => handleFile(e.target.files[0])} />
        </Button>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            Required: <code>script</code>, <code>question</code>
            <br />
            Optional (expected bucket): <code>meaning</code>, <code>meaning2</code>, <code>meaning3</code>
            <br />
            Optional (dialogue order, 1, 2, 3 in each script): <code>line_order</code>
          </Typography>
          <Button size="small" startIcon={<DownloadIcon />} onClick={downloadSampleCsv} sx={{ flexShrink: 0 }}>
            Download sample
          </Button>
        </Stack>
        {check?.ok && (
          <Alert severity="success">
            {plural(check.rows, "question", "questions")} in {plural(check.scripts, "script", "scripts")}.{" "}
            {plural(check.noExpectedBucket, "question has", "questions have")} no expected bucket.{" "}
            {check.hasLineOrder ? "Has dialogue order." : "No dialogue order."}
          </Alert>
        )}
        {check && !check.ok && (
          <Alert severity="error">
            This file has problems. Fix them and choose the file again.
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {check.errors.map((err, i) => (
                <li key={i}>
                  Row {err.row}
                  {err.script ? ` (${err.script})` : ""}: {err.message}
                </li>
              ))}
            </Box>
          </Alert>
        )}
        {check?.warnings.length > 0 && (
          <Alert severity="warning">
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {check.warnings.map((w, i) => (
                <li key={i}>{w.message}</li>
              ))}
            </Box>
          </Alert>
        )}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onCancel} disabled={adding}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!canAdd}>
            {adding ? <CircularProgress size={18} /> : "Add golden set"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

// Step 1 of run setup. The page loads golden sets (and passes `onSearch` for
// server-side search); this component only filters, shows and adds.
const GoldenSetPicker = ({ goldenSets, loading, value, onChange, onSearch, onAdd }) => {
  const [adding, setAdding] = useState(false);

  const handleAdd = async (input) => {
    const added = await onAdd(input);
    if (added) {
      onChange(added);
      setAdding(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Autocomplete
        options={goldenSets}
        loading={loading}
        value={value}
        onChange={(_, g) => onChange(g)}
        // Typing searches the server; clearing the box clears the search too.
        onInputChange={(_, text, reason) => {
          if (reason === "input") onSearch?.(text);
          if (reason === "clear") onSearch?.("");
        }}
        getOptionLabel={(g) => g.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        filterOptions={(options, { inputValue }) => options.filter((g) => matches(g, inputValue))}
        noOptionsText={
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <span>No golden set matches</span>
            <Button size="small" startIcon={<AddIcon />} onMouseDown={() => setAdding(true)}>
              Add a golden set
            </Button>
          </Stack>
        }
        renderOption={({ key, ...props }, g) => (
          <li key={key} {...props}>
            <Stack>
              <Typography variant="body2">{g.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {describeGoldenSet(g)}
              </Typography>
            </Stack>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Golden set" placeholder="Type a golden set or script name" />
        )}
      />
      {value && (
        <Typography variant="body2" color="text.secondary">
          {describeGoldenSet(value)}
        </Typography>
      )}
      {adding ? (
        <AddGoldenSetForm onAdd={handleAdd} onCancel={() => setAdding(false)} />
      ) : (
        <Box>
          <Button startIcon={<AddIcon />} onClick={() => setAdding(true)}>
            Add a golden set
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export default GoldenSetPicker;
